import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signOut as fbSignOut,
  linkWithCredential,
  updatePassword,
  EmailAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  type ConfirmationResult,
} from 'firebase/auth';
import { firebaseAuth } from './firebase';
import { supabase } from './supabase';
import { invalidate as invalidateCache } from './cache';

/* Phone-OTP authentication via Firebase Auth.
   Supabase only stores data; the signed-in identity comes from Firebase. */

let _confirmation: ConfirmationResult | null = null;
let _verifier: RecaptchaVerifier | null = null;

/** Lazily build an invisible reCAPTCHA bound to #recaptcha-container.
    The page using sendOtp must render <div id="recaptcha-container" />. */
function getVerifier(): RecaptchaVerifier {
  if (!_verifier) {
    _verifier = new RecaptchaVerifier(firebaseAuth, 'recaptcha-container', {
      size: 'invisible',
    });
  }
  return _verifier;
}

/** Send an SMS OTP to a phone number in E.164 form (e.g. +9198…). */
export async function sendOtp(phone: string) {
  _confirmation = await signInWithPhoneNumber(firebaseAuth, phone, getVerifier());
}

/** Verify the 6-digit code. `phone` is unused (kept for a stable signature). */
export async function verifyOtp(_phone: string, code: string) {
  if (!_confirmation) throw new Error('Request an OTP first.');
  const cred = await _confirmation.confirm(code);
  _confirmation = null;
  return cred.user;
}

export async function signOut() {
  // Drop every cached query so the next merchant never sees these rows.
  invalidateCache();
  await fbSignOut(firebaseAuth);
}

/** Resolve once Firebase has restored any persisted session. */
export function getCurrentUser() {
  return new Promise<import('firebase/auth').User | null>((resolve) => {
    const unsub = firebaseAuth.onAuthStateChanged((u) => {
      unsub();
      resolve(u);
    });
  });
}

/** The signed-in user's restaurant id, or null if they haven't onboarded yet. */
export async function myRestaurantId(): Promise<string | null> {
  const user = firebaseAuth.currentUser ?? (await getCurrentUser());
  if (!user) return null;
  const { data } = await supabase
    .from('merchants')
    .select('restaurantId')
    .eq('id', user.uid)
    .maybeSingle();
  return (data?.restaurantId as string | undefined) ?? null;
}

function friendlyAuthError(e: unknown): Error {
  const code = (e as { code?: string })?.code ?? '';
  switch (code) {
    case 'auth/email-already-in-use':
      return new Error('An account with this email already exists. Try logging in instead.');
    case 'auth/invalid-email':
      return new Error('That email address looks invalid.');
    case 'auth/weak-password':
      return new Error('Password is too weak — use at least 6 characters.');
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
      return new Error('Wrong email or password.');
    case 'auth/too-many-requests':
      return new Error('Too many attempts. Please wait a few minutes and try again.');
    case 'auth/user-disabled':
      return new Error('This account has been disabled.');
    case 'auth/operation-not-allowed':
      return new Error('This sign-in method is not enabled in Firebase Console → Authentication → Sign-in method.');
    default:
      return e instanceof Error ? e : new Error('Something went wrong. Please try again.');
  }
}

/** (Re)send the email-verification link to the signed-in user. No-op if the
    email is already verified. */
export async function sendVerificationEmail(): Promise<void> {
  const user = firebaseAuth.currentUser ?? (await getCurrentUser());
  if (!user) throw new Error('Please sign in first.');
  if (user.emailVerified) return;
  try {
    await sendEmailVerification(user);
  } catch (e) {
    throw friendlyAuthError(e);
  }
}

/** True when the signed-in user signed up with email+password but hasn't yet
    confirmed their email. Google/phone identities are treated as verified. */
export function needsEmailVerification(): boolean {
  const user = firebaseAuth.currentUser;
  if (!user) return false;
  const hasPassword = user.providerData.some((p) => p.providerId === 'password');
  return hasPassword && !user.emailVerified;
}

/** Create a new account with email + password (used by onboarding). */
export async function signUpWithEmail(email: string, password: string, displayName?: string) {
  try {
    const cred = await createUserWithEmailAndPassword(firebaseAuth, email, password);
    if (displayName) await updateProfile(cred.user, { displayName });
    // Fire off the verification email (soft — never blocks signup).
    try { await sendEmailVerification(cred.user); } catch { /* non-fatal */ }
    return cred.user;
  } catch (e) {
    throw friendlyAuthError(e);
  }
}

/** Sign in with email + password (same credentials as the POS / Captain apps). */
export async function signInWithEmail(email: string, password: string) {
  try {
    const cred = await signInWithEmailAndPassword(firebaseAuth, email, password);
    return cred.user;
  } catch (e) {
    throw friendlyAuthError(e);
  }
}

/** Send a password-reset email. */
export async function resetPassword(email: string) {
  try {
    await sendPasswordResetEmail(firebaseAuth, email);
  } catch (e) {
    throw friendlyAuthError(e);
  }
}

/** Link an email+password credential to the signed-in (phone) Firebase user,
    so the same account can log into the POS / Captain apps with email+password.
    If a password is already set, just updates it. */
export async function setAppPassword(email: string, password: string): Promise<void> {
  const user = firebaseAuth.currentUser ?? (await getCurrentUser());
  if (!user) throw new Error('Please sign in first.');
  try {
    await linkWithCredential(user, EmailAuthProvider.credential(email, password));
  } catch (e) {
    const code = (e as { code?: string })?.code ?? '';
    if (code === 'auth/provider-already-linked') {
      // Email already linked — just change the password.
      await updatePassword(user, password);
      return;
    }
    if (code === 'auth/email-already-in-use' || code === 'auth/credential-already-in-use') {
      throw new Error('That email is already used by another account.');
    }
    if (code === 'auth/weak-password') {
      throw new Error('Password is too weak — use at least 6 characters.');
    }
    if (code === 'auth/requires-recent-login') {
      throw new Error('For security, please log out, log back in, and try again.');
    }
    if (code === 'auth/invalid-email') {
      throw new Error('That email address looks invalid.');
    }
    throw e;
  }
}

/** True if the signed-in user already has an email+password login method. */
export async function hasAppPassword(): Promise<boolean> {
  const user = firebaseAuth.currentUser ?? (await getCurrentUser());
  return !!user?.providerData.some((p) => p.providerId === 'password');
}

/** Normalise to E.164-ish: keep digits, default to +91 if no country code. */
export function normalisePhone(input: string): string {
  const digits = input.replace(/[^\d]/g, '');
  if (input.trim().startsWith('+')) return '+' + digits;
  if (digits.length === 10) return '+91' + digits; // India default
  return '+' + digits;
}
